<script setup>
import { ref, computed, onMounted, watch, defineProps } from 'vue'
import axios from 'axios'

const props = defineProps({
  tableName: String,
  collapsed: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['toggle-collapse'])

const tableInfo = ref({})
const indexes = ref([])
const triggers = ref([])
const constraints = ref([])
const loading = ref(false)
const activeSection = ref('info')

const sections = [
  { id: 'info', label: 'Info', icon: 'fas fa-info-circle' },
  { id: 'indexes', label: 'Indexes', icon: 'fas fa-key' },
  { id: 'triggers', label: 'Triggers', icon: 'fas fa-bolt' },
  { id: 'constraints', label: 'Constraints', icon: 'fas fa-link' },
  { id: 'relations', label: 'Relations', icon: 'fas fa-project-diagram' }
]

const loadTableInfo = async () => {
  if (!props.tableName) return
  
  loading.value = true
  try {
    // Get enhanced table info
    const [infoResponse, indexesResponse, triggersResponse] = await Promise.all([
      axios.get(`/api/database-tables/${props.tableName}/info`),
      axios.get(`/api/database-tables/${props.tableName}/indexes`),
      axios.get(`/api/database-tables/${props.tableName}/triggers`)
    ])
    
    tableInfo.value = infoResponse.data
    indexes.value = indexesResponse.data.indexes || []
    triggers.value = triggersResponse.data.triggers || []
    constraints.value = [] // TODO: Add constraints endpoint
    
  } catch (error) {
    console.error('Error loading table info:', error)
    // Fallback to basic info
    try {
      const response = await axios.get(`/api/database-tables/${props.tableName}/structure`)
      tableInfo.value = response.data
    } catch (fallbackError) {
      console.error('Error loading fallback table info:', fallbackError)
    }
  } finally {
    loading.value = false
  }
}

const toggleCollapse = () => {
  emit('toggle-collapse')
}

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString()
}

watch(() => props.tableName, () => {
  if (props.tableName) {
    loadTableInfo()
  }
})

onMounted(() => {
  if (props.tableName) {
    loadTableInfo()
  }
})
</script>

<template>
  <div class="bg-white border-l border-gray-200 transition-all duration-300 flex flex-col"
       :class="collapsed ? 'w-12' : 'w-64'">
    
    <!-- Header -->
    <div class="p-2 border-b border-gray-200 flex items-center justify-between">
      <h3 v-if="!collapsed" class="text-sm font-semibold text-gray-900">Table Info</h3>
      <button 
        @click="toggleCollapse"
        class="text-gray-500 hover:text-gray-700 p-1"
        :title="collapsed ? 'Expand Panel' : 'Collapse Panel'"
      >
        <i :class="collapsed ? 'fas fa-chevron-left' : 'fas fa-chevron-right'"></i>
      </button>
    </div>

    <!-- Content -->
    <div v-if="!collapsed" class="flex-1 overflow-y-auto">
      <!-- Section Tabs -->
      <div class="border-b border-gray-200">
        <nav class="flex flex-col">
          <button
            v-for="section in sections"
            :key="section.id"
            @click="activeSection = section.id"
            class="flex items-center px-3 py-2 text-xs text-left hover:bg-gray-50 border-b border-gray-100"
            :class="{
              'bg-blue-50 text-blue-600 border-l-2 border-l-blue-500': activeSection === section.id,
              'text-gray-600': activeSection !== section.id
            }"
          >
            <i :class="section.icon" class="mr-2 text-xs"></i>
            {{ section.label }}
          </button>
        </nav>
      </div>

      <!-- Section Content -->
      <div class="p-3">
        <!-- Table Info Section -->
        <div v-if="activeSection === 'info'" class="space-y-3">
          <div v-if="loading" class="text-center py-4">
            <i class="fas fa-spinner fa-spin text-gray-400"></i>
          </div>
          
          <div v-else-if="tableName">
            <div class="space-y-2">
              <div class="bg-gray-50 p-2 rounded text-xs">
                <div class="font-medium text-gray-700 mb-1">Table Name</div>
                <div class="text-gray-900">{{ tableName }}</div>
              </div>
              
              <div class="bg-gray-50 p-2 rounded text-xs">
                <div class="font-medium text-gray-700 mb-1">Columns</div>
                <div class="text-gray-900">{{ tableInfo.column_count || tableInfo.columns?.length || 0 }}</div>
              </div>
              
              <div class="bg-gray-50 p-2 rounded text-xs">
                <div class="font-medium text-gray-700 mb-1">Rows</div>
                <div class="text-gray-900">{{ tableInfo.row_count?.toLocaleString() || 0 }}</div>
              </div>
              
              <div class="bg-gray-50 p-2 rounded text-xs">
                <div class="font-medium text-gray-700 mb-1">Engine</div>
                <div class="text-gray-900">{{ tableInfo.engine || 'InnoDB' }}</div>
              </div>
              
              <div class="bg-gray-50 p-2 rounded text-xs">
                <div class="font-medium text-gray-700 mb-1">Collation</div>
                <div class="text-gray-900">{{ tableInfo.collation || 'utf8mb4_unicode_ci' }}</div>
              </div>
              
              <div v-if="tableInfo.data_length" class="bg-gray-50 p-2 rounded text-xs">
                <div class="font-medium text-gray-700 mb-1">Data Size</div>
                <div class="text-gray-900">{{ formatBytes(tableInfo.data_length) }}</div>
              </div>
              
              <div v-if="tableInfo.index_length" class="bg-gray-50 p-2 rounded text-xs">
                <div class="font-medium text-gray-700 mb-1">Index Size</div>
                <div class="text-gray-900">{{ formatBytes(tableInfo.index_length) }}</div>
              </div>
              
              <div v-if="tableInfo.auto_increment" class="bg-gray-50 p-2 rounded text-xs">
                <div class="font-medium text-gray-700 mb-1">Auto Increment</div>
                <div class="text-gray-900">{{ tableInfo.auto_increment }}</div>
              </div>
              
              <div v-if="tableInfo.created_at" class="bg-gray-50 p-2 rounded text-xs">
                <div class="font-medium text-gray-700 mb-1">Created</div>
                <div class="text-gray-900">{{ formatDate(tableInfo.created_at) }}</div>
              </div>
            </div>
          </div>
          
          <div v-else class="text-center py-4 text-xs text-gray-500">
            Select a table to view info
          </div>
        </div>

        <!-- Indexes Section -->
        <div v-if="activeSection === 'indexes'" class="space-y-2">
          <div v-if="indexes.length === 0" class="text-center py-4 text-xs text-gray-500">
            No indexes found
          </div>
          
          <div v-else>
            <div 
              v-for="index in indexes" 
              :key="index.name"
              class="bg-gray-50 p-2 rounded text-xs"
            >
              <div class="flex items-center justify-between mb-1">
                <span class="font-medium text-gray-900">{{ index.name }}</span>
                <span 
                  class="px-1.5 py-0.5 rounded text-xs"
                  :class="index.unique ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'"
                >
                  {{ index.type }}
                </span>
              </div>
              <div class="text-gray-600">
                Columns: {{ index.columns.join(', ') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Triggers Section -->
        <div v-if="activeSection === 'triggers'" class="space-y-2">
          <div v-if="triggers.length === 0" class="text-center py-4 text-xs text-gray-500">
            No triggers found
          </div>
          
          <div v-else>
            <div 
              v-for="trigger in triggers" 
              :key="trigger.name"
              class="bg-gray-50 p-2 rounded text-xs"
            >
              <div class="flex items-center justify-between mb-1">
                <span class="font-medium text-gray-900">{{ trigger.name }}</span>
                <span class="px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-800">
                  {{ trigger.timing }} {{ trigger.event }}
                </span>
              </div>
              <div class="text-gray-600 text-xs">
                {{ trigger.statement.substring(0, 100) }}{{ trigger.statement.length > 100 ? '...' : '' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Constraints Section -->
        <div v-if="activeSection === 'constraints'" class="space-y-2">
          <div class="text-center py-4 text-xs text-gray-500">
            No constraints found
          </div>
        </div>

        <!-- Relations Section -->
        <div v-if="activeSection === 'relations'" class="space-y-2">
          <div class="text-center py-4 text-xs text-gray-500">
            No relations found
          </div>
        </div>
      </div>
    </div>

    <!-- Collapsed State -->
    <div v-else class="flex-1 flex flex-col items-center py-4 space-y-3">
      <button
        v-for="section in sections"
        :key="section.id"
        @click="activeSection = section.id; toggleCollapse()"
        class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
        :class="{
          'text-blue-600 bg-blue-50': activeSection === section.id
        }"
        :title="section.label"
      >
        <i :class="section.icon" class="text-sm"></i>
      </button>
    </div>
  </div>
</template> 